import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Building2, CreditCard } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { fetchUserOrganizationsQueryOptions } from "@/api/organizations";
import { fetchPlatformSubscriptionQueryOptions } from "@/api/platformBilling";
import { BillingContent } from "@/components/BillingContent";
import { Button } from "@/components/ui/button";
import { useApiParameters } from "@/hooks/useApiParameters";

const renewSubscriptionSearchSchema = z.object({
	orgId: z.string(),
});

export const Route = createFileRoute("/_auth/app/_app/org/renew-subscription")({
	validateSearch: renewSubscriptionSearchSchema,
	loaderDeps: ({ search }) => ({ orgId: search.orgId }),
	loader: async ({ context, deps }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId } = deps;
		// Ensure organizations are loaded so we can derive org name
		context.queryClient.ensureQueryData(
			fetchUserOrganizationsQueryOptions(userId, token),
		);
		// Force fresh Stripe subscription sync on every page load
		context.queryClient.fetchQuery(
			fetchPlatformSubscriptionQueryOptions(userId, orgId),
		);
		return { userId, token };
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const { orgId } = Route.useSearch();

	// Fetch organizations to get the org name
	const { data: organizations } = useSuspenseQuery(
		fetchUserOrganizationsQueryOptions(userId, token),
	);

	const organization = organizations.find((org) => org.id === orgId);
	const organizationName = organization?.name ?? orgId;

	return (
		<div className="min-h-screen bg-base-200">
			{/* Header */}
			<div className="bg-base-100 border-b border-base-300 sticky top-0 z-10">
				<div className="max-w-6xl mx-auto px-4 py-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-4">
							<Link
								to="/app/select-organization"
								search={{ skipAutoRedirect: true }}
							>
								<Button variant="ghost" size="sm">
									<ArrowLeft className="h-4 w-4 mr-2" />
									{t("common.back")}
								</Button>
							</Link>
							<div className="h-6 w-px bg-base-300" />
							<div className="flex items-center gap-2">
								<div className="p-2 bg-primary/10 rounded-lg">
									<CreditCard className="h-5 w-5 text-primary" />
								</div>
								<div>
									<h1 className="text-lg font-semibold">
										{t("renewSubscription.title")}
									</h1>
									<div className="flex items-center gap-1 text-sm text-base-content/60">
										<Building2 className="h-3 w-3" />
										<span>{organizationName}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Content */}
			<div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
				{/* Plan Selection Header */}
				<p className="text-base-content/70">
					{t("renewSubscription.choosePlanDescription")}
				</p>

				<BillingContent userId={userId} orgId={orgId} />
			</div>
		</div>
	);
}
